import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LogoutButton from './authentication/LogoutButton';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContexts';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../pages/app/protected/firebase';

// Import icon image paths
import HomeIcon from '../assets/icons/home.png';
import CartIcon from '../assets/icons/cart.png';
import InventoryIcon from '../assets/icons/inventory.png';
import ChartIcon from '../assets/icons/chart.png';
import SettingsIcon from '../assets/icons/settings.png';
import HelpIcon from '../assets/icons/help.png';
import KitchenIcon from '../assets/icons/chef.png';
import DeliveryIcon from '../assets/icons/delivery.png';
import QueueIcon from '../assets/icons/queue.png';
import historyIcon from '../assets/icons/history.png';
import myAccountIcon from '../assets/icons/accounting.png';

// Define base menu items
export const baseNavItems = [
  { name: 'Home', src: HomeIcon, to: '/dashboard', alwaysVisible: true },
  { name: 'Cashier', src: CartIcon, to: '/product-view', alwaysVisible: false },
];

export const baseFinanceItems = [
  { name: 'History', src: historyIcon, to: '/history', alwaysVisible: false },
  { name: 'Reports', src: ChartIcon, to: '/reports', alwaysVisible: false },
  { name: 'Accounting', src: myAccountIcon, to: '/accounting', new: false, soon: false, alwaysVisible: false },
];

export const baseDisplayItems = [
  { name: 'Kitchen', src: KitchenIcon, to: '/online', alwaysVisible: false },
  { name: 'Orders', src: DeliveryIcon, to: '/online', alwaysVisible: false },
  { name: 'Pickup Status', src: QueueIcon, to: '/pickup-status', alwaysVisible: false },
];

export const baseToolsItems = [
  { name: 'Inventory', src: InventoryIcon, to: '/inventory', alwaysVisible: false },
  { name: 'Help', src: HelpIcon, to: 'https://cashmate.tawk.help', alwaysVisible: true },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading, user ,companyId} = useAuth();
  const { open, setOpen } = useSidebar();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sidebarSettings, setSidebarSettings] = useState({
    navItems: {},
    financeItems: {},
    displayItems: {},
    toolsItems: {}
  });

  const [stallType, setStallType] = useState(null);

  // Load sidebar settings
useEffect(() => {
  const loadSettings = async () => {
    if (!user || !companyId) return;

    try {
      // Load sidebar settings
      const settingsRef = doc(db, 'companies', companyId, 'companySettings', 'sidebar');
      const settingsSnap = await getDoc(settingsRef);

      // Load company profile for stallType
      const stallRef = doc(db, 'companies', companyId, 'companySettings', 'info');
      const stallSnap = await getDoc(stallRef);

      if (stallSnap.exists()) {
        const data = stallSnap.data();
        if (data.stallType) {
          setStallType(data.stallType.toLowerCase());
        }
      }

      if (settingsSnap.exists()) {
        setSidebarSettings(settingsSnap.data());
      } else {
        const defaultSettings = {
          navItems: baseNavItems.reduce((acc, item) => ({
            ...acc,
            [item.name]: !item.alwaysVisible
          }), {}),
          financeItems: baseFinanceItems.reduce((acc, item) => ({
            ...acc,
            [item.name]: !item.alwaysVisible
          }), {}),
          displayItems: baseDisplayItems.reduce((acc, item) => ({
            ...acc,
            [item.name]: !item.alwaysVisible
          }), {}),
          toolsItems: baseToolsItems.reduce((acc, item) => ({
            ...acc,
            [item.name]: true
          }), {})
        };
        setSidebarSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error loading sidebar settings:', error);
    }
  };

  loadSettings();
}, [user, companyId]);

  // Filter items based on settings
  const navItems = baseNavItems.filter(item => 
    item.alwaysVisible || (sidebarSettings.navItems && sidebarSettings.navItems[item.name])
  );

  const financeItems = baseFinanceItems.filter(item => 
    item.alwaysVisible || (sidebarSettings.financeItems && sidebarSettings.financeItems[item.name])
  );

// 🔹 Build display items dynamically based on stallType
const getDisplayItems = () => {
  let items = [];

  if (stallType === 'fnb') {
    items = [
      { name: 'Kitchen', src: KitchenIcon, to: '/online', alwaysVisible: true },
      { name: 'Pickup Status', src: QueueIcon, to: '/pickup-status', alwaysVisible: true },
    ];
  } else if (stallType === 'non-fnb') {
    items = [
      { name: 'Online Order', src: QueueIcon, to: '/online', alwaysVisible: true },
      { name: 'Pickup Status', src: QueueIcon, to: '/pickup-status', alwaysVisible: true },
    ];
  } else {
    // Default fallback while loading
    items = baseDisplayItems;
  }

  // Ensure visibility settings don’t remove new items accidentally
  return items.filter(
    item =>
      item.alwaysVisible ||
      (sidebarSettings.displayItems && sidebarSettings.displayItems[item.name] !== false)
  );
};

const [displayItems, setDisplayItems] = useState(getDisplayItems());

useEffect(() => {
  setDisplayItems(getDisplayItems());
}, [stallType, sidebarSettings]);

  const toolsItems = baseToolsItems.filter(item => 
    item.alwaysVisible || (sidebarSettings.toolsItems && sidebarSettings.toolsItems[item.name])
  );

  const handleSettingsClick = () => {
    if (!open) setOpen(true);
    setSettingsOpen(true);
  };

  useEffect(() => {
    const settingsPaths = [
      '/my-account', '/company-profile', '/company-settings', 
      '/billing', '/file-manager', '/members', '/backup'
    ];
    const isSettingsPage = settingsPaths.some(path => location.pathname.includes(path));

    if (location.pathname === '/product-view' || location.pathname === '/online') {
      setOpen(false);
    } else if (!isSettingsPage) {
      setSettingsOpen(false);
    }
  }, [location.pathname]);

  if (loading) return null;
  if (!isAuthenticated) {
    navigate('/login', { replace: true });
    return null;
  }

  // State for mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Mobile Hamburger Button - Fixed at top left */}
      <div className="fixed top-0 left-0 right-0 z-50 h-16 bg-[#121212] lg:hidden">
        <div className="flex items-center h-full px-4">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-md text-white focus:outline-none hover:bg-white/10 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
          <img src="/images/logo.png" alt="CashMate Logo" className="h-8 w-auto ml-2" />
        </div>
      </div>

      {/* Overlay - Only visible when mobile menu is open */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside 
        className={`fixed top-0 left-0 h-screen bg-[#121212] text-white border-r border-gray-800 transition-all duration-300 transform ${
          open ? 'w-64' : 'w-20'
        } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 z-50`}
      >

      {/* Collapse Button - Hidden on mobile */}
      <button
        onClick={() => setOpen(!open)}
        className="absolute top-6 -right-3 bg-[#1a1a1a] rounded-full p-1 shadow-md hover:bg-[#2b2b2b] transition hidden lg:block"
      >
        {open ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </button>

      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-8">
        {/* Mobile Close Button - Only visible on small screens */}
        <button 
          onClick={() => setMobileMenuOpen(false)}
          className="lg:hidden absolute right-4 top-6 text-gray-400 hover:text-white"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img src="/images/logo.png" alt="CashMate Logo" className="h-10 w-auto" />
        {open && (
          <span className="text-xl tracking-widest" style={{ fontFamily: 'Century Gothic', letterSpacing: '5px' }}>
            CASHMATE
          </span>
        )}
      </div>

      {/* Navigation Menu */}
      <div className="flex flex-col h-[calc(100%-140px)] overflow-y-auto pr-1">
        {/* Custom scrollbar styling */}
        <style>{`
          .overflow-y-auto::-webkit-scrollbar {
            width: 6px;
          }
          .overflow-y-auto::-webkit-scrollbar-track {
            background: rgba(75, 85, 99, 0.1);
            border-radius: 3px;
          }
          .overflow-y-auto::-webkit-scrollbar-thumb {
            background-color: rgba(156, 163, 175, 0.5);
            border-radius: 3px;
          }
          .overflow-y-auto::-webkit-scrollbar-thumb:hover {
            background-color: rgba(147, 197, 253, 0.7);
          }
        `}</style>
        <div className="flex flex-col space-y-2 px-2">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.to}
              className={`flex items-center gap-4 px-4 py-3 rounded-2xl font-semibold transition ${
                location.pathname.startsWith(item.to) ? 'bg-[#a46bf5] text-white' : 'hover:bg-white/10 text-gray-300'
              }`}
            >
              <div className="flex items-center gap-4">
                <img src={item.src} alt={`${item.name} icon`} className="w-5 h-5" />
                {open && <span className="text-md">{item.name}</span>}
              </div>
              {open && item.new && (
                <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">New!</span>
              )}
              {open && item.soon && (
                <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">Soon!</span>
              )}
            </Link>
          ))}
        </div>

        {/* Finance Label */}
        {open && (
          <div className="mt-8 mb-2 px-6">
            <p className="text-xs font-light uppercase tracking-widest text-gray-400">Finance</p>
          </div>
        )}
        <div className="flex flex-col space-y-2 px-2">
          {financeItems.map((item) => (
            <Link
              key={item.name}
              to={item.to}
              className={`flex items-center justify-between gap-4 px-4 py-3 rounded-2xl font-semibold transition ${
                location.pathname.startsWith(item.to) ? 'bg-[#a46bf5] text-white' : 'hover:bg-white/10 text-gray-300'
              }`}
            >
              <div className="flex items-center gap-4">
                <img src={item.src} alt={`${item.name} icon`} className="w-5 h-5" />
                {open && <span className="text-md">{item.name}</span>}
              </div>
              {open && item.new && (
                <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">New!</span>
              )}
              {open && item.soon && (
                <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">Soon!</span>
              )}
            </Link>
          ))}
        </div>

        {/* Display Label */}
        {open && (
          <div className="mt-8 mb-2 px-6">
            <p className="text-xs font-light uppercase tracking-widest text-gray-400">Display</p>
          </div>
        )}
        <div className="flex flex-col space-y-2 px-2">
          {displayItems.map((item) => {
            const linkProps = {
              className: `flex items-center gap-4 px-4 py-3 rounded-2xl font-semibold transition ${
                location.pathname.startsWith(item.to) ? 'bg-[#a46bf5] text-white' : 'hover:bg-white/10 text-gray-300'
              }`
            };

            // Update name and icon based on stall type
            const displayName = stallType === 'retail' ? 'Orders' : item.name;
            const displayIcon = stallType === 'retail' ? DeliveryIcon : item.src;

            const content = (
              <>
                <div className="flex items-center gap-4">
                  <img src={displayIcon} alt={`${displayName} icon`} className="w-5 h-5" />
                  {open && <span className="text-md">{displayName}</span>}
                </div>
                {open && item.new && (
                  <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">New!</span>
                )}
                {open && item.soon && (
                  <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">Soon!</span>
                )}
              </>
            );

            return (
              <Link key={item.name} to={item.to} {...linkProps}>
                {content}
              </Link>
            );
          })}
        </div>

        {/* Tools Label */}
        {open && (
          <div className="mt-8 mb-2 px-6">
            <p className="text-xs font-light uppercase tracking-widest text-gray-400">Tools</p>
          </div>
        )}
        
        {/* Tools Section */}
        <div className="flex flex-col space-y-2 px-2">
          {toolsItems.map((item) => (
            <Link
              key={item.name}
              to={item.to}
              className={`flex items-center justify-between gap-4 px-4 py-3 rounded-2xl font-semibold transition ${
                location.pathname.startsWith(item.to) ? 'bg-[#a46bf5] text-white' : 'hover:bg-white/10 text-gray-300'
              }`}
            >
              <div className="flex items-center gap-4">
                <img src={item.src} alt={`${item.name} icon`} className="w-5 h-5" />
                {open && <span className="text-md">{item.name}</span>}
              </div>
              {open && item.new && (
                <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">New!</span>
              )}
            </Link>
          ))}
        </div>

        {/* Settings Dropdown */}
        <div className="px-2">
          <button
            onClick={handleSettingsClick}
            className={`flex items-center justify-between w-full px-4 py-3 rounded-2xl font-semibold transition ${
              location.pathname.startsWith('/settings') || settingsOpen ? 'bg-[#a46bf5] text-white' : 'hover:bg-white/10 text-gray-300'
            }`}
          >
            <div className="flex items-center gap-4">
              <img src={SettingsIcon} alt="Settings" className="w-5 h-5" />
              {open && <span className="text-md">Settings</span>}
            </div>
            {open && (
              <svg className={`w-4 h-4 transition-transform ${settingsOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
        </div>

        {/* Settings Dropdown Links */}
        {settingsOpen && open && (
          <div className="ml-10 flex flex-col space-y-2 mt-2">
            <Link to="/my-account" className={`text-sm ${location.pathname.startsWith('/my-account') ? 'text-white' : 'hover:text-white text-gray-400'}`}>My Account</Link>
            <Link to="/company-profile" className={`text-sm ${location.pathname.startsWith('/company-profile') ? 'text-white' : 'hover:text-white text-gray-400'}`}>Company Profile</Link>
            <Link to="/company-settings" className={`text-sm ${location.pathname.startsWith('/company-settings') ? 'text-white' : 'hover:text-white text-gray-400'}`}>Company Settings</Link>
            <Link to="/billing" className={`text-sm ${location.pathname.startsWith('/billing') ? 'text-white' : 'hover:text-white text-gray-400'}`}>Billing & Subscription</Link>
            <Link to="/file-manager" className={`text-sm ${location.pathname.startsWith('/file-manager') ? 'text-white' : 'hover:text-white text-gray-400'}`}>File Manager</Link>
            {/* <Link to="/members" className={`text-sm ${location.pathname.startsWith('/members') ? 'text-white' : 'hover:text-white text-gray-400'}`}>Members</Link> */}
            <Link to="/backup" className={`text-sm ${location.pathname.startsWith('/backup') ? 'text-white' : 'hover:text-white text-gray-400'}`}>Backup & Restore</Link>
          
          </div>
        )}

        {/* Divider */}
        {open && <div className="my-6 border-t border-gray-700 mx-4"></div>}

        {/* Logout */}
        <div className="mb-6 px-2">
          <LogoutButton open={open} />
        </div>
      </div>
    </aside>
    </>
  );
}
